import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Role permission definitions (matching backend)
 */
const ROLE_PERMISSIONS = {
  system_admin: {
    tracks: ['read', 'create', 'update', 'delete', 'manage_base'],
    domains: ['read', 'create', 'update', 'delete', 'manage_base'],
    strategies: ['read', 'create', 'update', 'delete', 'manage_base'],
    risk_codes: ['read', 'create', 'update', 'delete', 'manage_base'],
    agent_prompts: ['read', 'create', 'update', 'delete', 'manage_base'],
    assessment_instruments: ['read', 'create', 'update', 'delete', 'manage_base'],
    intervention_configs: ['read', 'create', 'update', 'delete', 'manage_base'],
    patients: ['read', 'create', 'update', 'delete'],
    analytics: ['read', 'export'],
    composition: ['read', 'preview'],
    admin_users: ['read', 'create', 'update', 'delete'],
    audit_log: ['read', 'export']
  },
  clinical_designer: {
    tracks: ['read', 'create', 'update', 'delete'],
    domains: ['read', 'create', 'update', 'delete'],
    strategies: ['read', 'create', 'update', 'delete'],
    risk_codes: ['read', 'create', 'update', 'delete'],
    agent_prompts: ['read', 'create', 'update', 'delete'],
    assessment_instruments: ['read', 'create', 'update', 'delete'],
    intervention_configs: ['read', 'create', 'update', 'delete'],
    patients: ['read', 'create', 'update'],
    analytics: ['read'],
    composition: ['read', 'preview'],
    admin_users: [],
    audit_log: ['read']
  },
  researcher: {
    tracks: ['read'],
    domains: ['read'],
    strategies: ['read'],
    risk_codes: ['read'],
    agent_prompts: ['read'],
    assessment_instruments: ['read'],
    intervention_configs: ['read'],
    patients: ['read'],
    analytics: ['read', 'export'],
    composition: ['read', 'preview'],
    admin_users: [],
    audit_log: ['read']
  },
  administrator: {
    tracks: ['read', 'create', 'update', 'delete'],
    domains: ['read', 'create', 'update', 'delete'],
    strategies: ['read', 'create', 'update', 'delete'],
    risk_codes: ['read', 'create', 'update', 'delete'],
    agent_prompts: ['read', 'create', 'update', 'delete'],
    assessment_instruments: ['read', 'create', 'update', 'delete'],
    intervention_configs: ['read', 'create', 'update', 'delete'],
    patients: ['read', 'create', 'update', 'delete'],
    analytics: ['read', 'export'],
    composition: ['read', 'preview'],
    admin_users: ['read', 'create', 'update', 'delete'],
    audit_log: ['read', 'export']
  }
}

export const AuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Check current session status via API
   */
  const checkSession = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch('/api/admin/session', {
        method: 'GET',
        credentials: 'include',
      })

      const data = await response.json()

      if (data.authenticated && data.adminUser) {
        setAdminUser(data.adminUser)
      } else {
        setAdminUser(null)
      }
    } catch (err) {
      console.error('Error checking session:', err)
      setAdminUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Check session on mount
  useEffect(() => {
    checkSession()
  }, [checkSession])

  /**
   * Handle OAuth callback data
   * Called by AuthCallback component after successful OAuth flow
   */
  const handleCallback = useCallback(async (callbackData) => {
    if (callbackData.success && callbackData.adminUser) {
      setAdminUser(callbackData.adminUser)
      setError(null)
    }
  }, [])

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include',
      })
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      setAdminUser(null)
    }
  }, [])

  /**
   * Check if user has a specific permission
   * @param {string} resource - The resource type
   * @param {string} action - The action (read, create, update, delete)
   * @returns {boolean}
   */
  const hasPermission = useCallback((resource, action) => {
    if (!adminUser) return false
    const permissions = ROLE_PERMISSIONS[adminUser.role]?.[resource] || []
    return permissions.includes(action)
  }, [adminUser])

  /**
   * Check if user has any of the specified roles
   * @param {string|Array} roles - Required role(s)
   * @returns {boolean}
   */
  const hasRole = useCallback((roles) => {
    if (!adminUser) return false
    const allowedRoles = Array.isArray(roles) ? roles : [roles]
    return allowedRoles.includes(adminUser.role)
  }, [adminUser])

  /**
   * Check if user can access a route based on required permissions
   * @param {Object} route - Route config with requiredPermission
   * @returns {boolean}
   */
  const canAccessRoute = useCallback((route) => {
    if (!route.requiredPermission) return true
    const { resource, action } = route.requiredPermission
    return hasPermission(resource, action)
  }, [hasPermission])

  /**
   * Refresh admin user data from /me endpoint
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/me', {
        method: 'GET',
        credentials: 'include',
      })

      if (response.ok) {
        const user = await response.json()
        setAdminUser(user)
      }
    } catch (err) {
      console.error('Error refreshing admin user:', err)
    }
  }, [])

  /**
   * Check if user can manage base content (edit/delete canonical Sanexus content)
   * @param {string} resource - The resource type
   * @returns {boolean}
   */
  const canManageBaseContent = useCallback((resource) => {
    if (!adminUser) return false
    return ROLE_PERMISSIONS[adminUser.role]?.[resource]?.includes('manage_base') || false
  }, [adminUser])

  const value = {
    adminUser,
    loading,
    error,
    hasPermission,
    hasRole,
    canAccessRoute,
    canManageBaseContent,
    refreshUser,
    checkSession,
    handleCallback,
    logout,
    isAuthenticated: !!adminUser,
    isSystemAdmin: adminUser?.role === 'system_admin',
    isAdministrator: adminUser?.role === 'administrator',
    isResearcher: adminUser?.role === 'researcher',
    isClinicalDesigner: adminUser?.role === 'clinical_designer'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
